import React from 'react'
import { LevelData, LEVEL_UP_APPLICATION_URL } from '../data/levelData'

interface LevelInfoCardProps {
  data: LevelData
  isVisible: boolean
  isCurrentLevel: boolean
  onMouseEnter?: () => void
  onMouseLeave?: () => void
}

const LevelInfoCard: React.FC<LevelInfoCardProps> = ({ 
  data, 
  isVisible, 
  isCurrentLevel, 
  onMouseEnter, 
  onMouseLeave 
}) => {
  const handleLevelUpApplication = () => {
    window.open(LEVEL_UP_APPLICATION_URL, '_blank', 'noopener,noreferrer')
  }

  if (!isVisible) return null

  return (
    <div 
      className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-[9999] animate-in fade-in duration-200"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="w-80 bg-white rounded-2xl shadow-xl border-2 border-primary-100 overflow-hidden">
        {/* 卡片標題 */}
        <div className={`p-4 bg-gradient-to-r ${data.gradientClass} text-white`}>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-bold">LV{data.level} - {data.name}</h4>
              <p className="text-sm opacity-90">{data.description}</p>
            </div>
            {isCurrentLevel && (
              <div className="text-2xl animate-pulse">✨</div>
            )}
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* 時薪資訊 */}
          <div className="bg-primary-50 rounded-lg p-3 border border-primary-200">
            <div className="text-sm text-primary-600 font-medium mb-1">時薪</div>
            <div className="text-xl font-bold text-primary-700">NT$ {data.hourlyRate}</div>
          </div>

          {/* 可申請升等時間 */}
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
            <div className="text-sm text-amber-600 font-medium mb-1">⏰ 可申請升等時間</div>
            <div className="text-sm text-amber-700 font-semibold">{data.timeRequirement}</div>
          </div>

          {/* 核心要求 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="text-sm font-semibold text-blue-600">核心要求</span>
            </div>
            <ul className="space-y-1">
              {data.requirements.map((req, index) => (
                <li key={index} className="text-sm text-gray-700 pl-4 relative">
                  <span className="absolute left-0 text-blue-500 font-bold">▸</span>
                  {req}
                </li>
              ))}
            </ul>
          </div>

          {/* 福利制度 */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <span className="text-sm font-semibold text-green-600">福利制度</span>
            </div>
            <ul className="space-y-1">
              {data.benefits.map((benefit, index) => (
                <li key={index} className="text-sm text-gray-700 pl-4 relative">
                  <span className="absolute left-0 text-green-500 font-bold">▸</span>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          {/* 升等申請按鈕 */}
          {!isCurrentLevel && (
            <div className="pt-2 border-t border-gray-200">
              <button
                onClick={handleLevelUpApplication}
                className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white font-semibold py-3 px-4 rounded-lg hover:from-primary-600 hover:to-primary-700 transition-all duration-200 transform hover:scale-105 shadow-md hover:shadow-lg"
              >
                🚀 申請升等
              </button>
            </div>
          )}

          {/* 當前等級標示 */}
          {isCurrentLevel && (
            <div className="pt-2 border-t border-primary-200">
              <div className="bg-gradient-to-r from-primary-100 to-primary-200 text-primary-700 font-semibold py-3 px-4 rounded-lg text-center border border-primary-300">
                🎯 您目前的職等
              </div>
            </div>
          )}
        </div>

        {/* 特殊等級標記 */}
        {data.isLegendary && (
          <div className="absolute top-2 right-2 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-sm font-bold animate-pulse">
            ✨
          </div>
        )}
        
        {data.isPartner && (
          <div className="absolute top-2 right-2 w-8 h-8 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full flex items-center justify-center text-white text-sm font-bold animate-pulse">
            👑
          </div>
        )}
      </div>
    </div>
  )
}

export default LevelInfoCard
